import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight } from "lucide-react";

interface GettingStartedItem {
  title: string;
  description: string;
  articleId?: string;
}

interface GettingStartedSectionProps {
  userType: "buyer" | "seller";
  onArticleClick: (articleId: string) => void;
}

const GettingStartedSection = ({ userType, onArticleClick }: GettingStartedSectionProps) => {
  const sellerSteps: GettingStartedItem[] = [
    {
      title: "Create Your Account",
      description: "Set up your profile and verify your email",
    },
    {
      title: "Upload Your First Property",
      description: "Add property details, photos, and pricing",
    },
    {
      title: "Share Your Listings",
      description: "Get your unique shareable link and start marketing",
    },
    {
      title: "Manage Notifications",
      description: "Track buyer interest and property views",
    },
  ];

  const buyerSteps: GettingStartedItem[] = [
    {
      title: "Create Your Account",
      description: "Sign up and set your investment preferences",
    },
    {
      title: "Search Properties",
      description: "Use filters to find properties matching your criteria",
    },
    {
      title: "Save & Make Offers",
      description: "Save favorite properties and submit offers to sellers",
    },
    {
      title: "Follow Favorite Sellers",
      description: "Follow trusted sellers via the menu to see their new listings first",
    },
  ];

  const steps = userType === "buyer" ? buyerSteps : sellerSteps;

  return (
    <Card className="bg-gradient-to-br from-primary/5 to-background border-primary/20">
      <CardHeader>
        <CardTitle className="text-2xl">Getting Started Guide</CardTitle>
        <p className="text-muted-foreground">
          Follow these steps to get the most out of GoDyspo
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 rounded-lg bg-background/50 hover:bg-background transition-colors"
            >
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
              <CheckCircle2 className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
            </div>
          ))}
        </div>
        <Button className="w-full mt-6" size="lg">
          View All Guides
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default GettingStartedSection;
